import type { Employee, RoomId, SaveState } from './GameState';
import { ROOMS, type RoomObject } from '../world/rooms';
import { DIRS, canStep, type Tile } from '../world/Grid';
import { route, tilePoint, pointTile, distance as pixelDistance, segmentClear, isFree, withActors, type Point } from '../world/Movement';

export interface StaffLocation extends Tile { room: RoomId }
export interface StaffNavTarget extends StaffLocation {
  objectId: string;
  purpose: 'work' | 'delivery';
}
export interface StaffNavStep {
  from: StaffLocation;
  to: StaffLocation;
  progress: number;
}
export interface StaffDestination { room: RoomId; objectId: string }

/** A normal-speed employee crosses one floor tile in 0.75 real seconds. */
export const STAFF_TILES_PER_MINUTE = 4;
const locationKey = (p: StaffLocation): string => `${p.room}:${p.x},${p.y}`;
const same = (a: StaffLocation, b: StaffLocation): boolean => a.room === b.room && a.x === b.x && a.y === b.y;
type ContinuousEmployee = Employee & { position?: { room: RoomId; x: number; y: number }; continuousPath?: Point[] };
const continuous = (e: Employee): ContinuousEmployee => e as ContinuousEmployee;

function syncPosition(e: Employee): void {
  const c = continuous(e);
  if (!e.location) return;
  if (!c.position || c.position.room !== e.location.room || pointTile(c.position).x !== e.location.x || pointTile(c.position).y !== e.location.y) {
    c.position = { room: e.location.room, ...tilePoint(e.location) };
  }
}

function objects(state: SaveState, room: RoomId): RoomObject[] {
  return ROOMS[room].objects.filter((o) => !o.requiresSkill || state.skills.includes(o.requiresSkill));
}

export function staffObject(state: SaveState, target: StaffDestination): RoomObject | undefined {
  return objects(state, target.room).find((o) => o.id === target.objectId);
}

export function floorWalkable(state: SaveState, room: RoomId, x: number, y: number): boolean {
  const def = ROOMS[room];
  return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < def.cols && y < def.rows &&
    !objects(state, room).some((o) => o.kind !== 'door' && x >= o.x && x < o.x + o.w && y >= o.y && y < o.y + o.h);
}

/** Actual actor tiles and the two ends of an in-flight step are occupied. */
export function staffOccupies(state: SaveState, room: RoomId, x: number, y: number, ignoreId?: string): boolean {
  const here = { room, x, y };
  return state.staff.employees.some((e) => e.id !== ignoreId && e.presentToday &&
    [e.location, e.navStep?.from, e.navStep?.to].some((p) => p && same(here, p)));
}

function canOccupy(state: SaveState, employee: Employee, p: StaffLocation): boolean {
  if (!floorWalkable(state, p.room, p.x, p.y) || staffOccupies(state, p.room, p.x, p.y, employee.id)) return false;
  if (state.room === p.room && [state.playerTile, state.playerMovingTo].some((t) => t && t.x === p.x && t.y === p.y)) return false;
  return !state.staff.employees.some((e) => e.id !== employee.id && e.presentToday && e.navTarget && same(e.navTarget, p));
}

/** Cardinally adjacent perimeter positions; station corners are not work tiles. */
export function stationPositions(state: SaveState, destination: StaffDestination): StaffLocation[] {
  const station = staffObject(state, destination);
  if (!station) return [];
  const result: StaffLocation[] = [];
  for (let y = station.y - 1; y <= station.y + station.h; y++) {
    for (let x = station.x - 1; x <= station.x + station.w; x++) {
      const insideX = x >= station.x && x < station.x + station.w;
      const insideY = y >= station.y && y < station.y + station.h;
      if (insideX === insideY) continue;
      if (!floorWalkable(state, destination.room, x, y)) continue;
      // Reserving a doorway would strand another employee in the other room.
      if (objects(state, destination.room).some((o) => o.kind === 'door' && o.x === x && o.y === y)) continue;
      result.push({ room: destination.room, x, y });
    }
  }
  return result;
}

export function clearStaffNavigation(employee: Employee): void {
  employee.navTarget = undefined;
  employee.navPath = undefined;
  // Do not snap a moving employee back when its task is changed. Complete the
  // current legal step; a replacement route starts from that step's endpoint.
  employee.navigationBlocked = null;
  continuous(employee).continuousPath = undefined;
}

/** Only hire/load recovery may choose a replacement actor tile. */
export function ensureStaffPosition(state: SaveState, employee: Employee, recoverOverlap = false): boolean {
  const p = employee.location;
  // A dynamic obstruction never teleports an actor during an ordinary tick.
  // Startup/load recovery may explicitly repair overlapping saved positions.
  const actual=employee.position??(p?{room:p.room,...tilePoint(p)}:undefined);
  const overlaps=actual && (state.staff.employees.some(e=>e.id!==employee.id&&e.presentToday&&(e.position?.room??e.location?.room)===actual.room&&Math.hypot((e.position??tilePoint(e.location!)).x-actual.x,(e.position??tilePoint(e.location!)).y-actual.y)<20) || state.room===actual.room&&Math.hypot(state.playerPosition.x-actual.x,state.playerPosition.y-actual.y)<20);
  if(p&&ROOMS[p.room]&&floorWalkable(state,p.room,p.x,p.y)&&actual&&isFree(ROOMS[p.room],state.skills,actual)&&(!recoverOverlap||!overlaps))return true;
  const room = p && ROOMS[p.room] ? p.room : employee.station === 'cellar' ? 'cellar' : employee.station === 'vat' ? 'creamery' : 'barn';
  const start = p && p.room === room ? p : ROOMS[room].spawn;
  const candidates: StaffLocation[] = [];
  for (let y = 0; y < ROOMS[room].rows; y++) {
    for (let x = 0; x < ROOMS[room].cols; x++) {
      const candidate = { room, x, y };
      if (canOccupy(state, employee, candidate)) candidates.push(candidate);
    }
  }
  candidates.sort((a, b) => Math.abs(a.x - start.x) + Math.abs(a.y - start.y) - Math.abs(b.x - start.x) - Math.abs(b.y - start.y));
  if (!candidates.length) {
    employee.navigationBlocked = 'No free floor space';
    return false;
  }
  employee.location = candidates[0];
  employee.jobPosition = { x: employee.location.x, y: employee.location.y };
  employee.navStep = undefined;
  employee.navPath = undefined;
  continuous(employee).position = { room, ...tilePoint(employee.location) };
  return true;
}

/** Dijkstra search over the room graph, including explicit doorway edges. */
export function findStaffPath(state: SaveState, employee: Employee, start: StaffLocation, goal: StaffLocation): StaffLocation[] | null {
  if (same(start, goal)) return [];
  const firstKey = locationKey(start);
  const costs = new Map<string, number>([[firstKey, 0]]);
  const parents = new Map<string, StaffLocation>();
  const open = [{ p: start, cost: 0 }];
  while (open.length) {
    let best = 0;
    for (let i = 1; i < open.length; i++) if (open[i].cost < open[best].cost) best = i;
    const current = open.splice(best, 1)[0];
    const key = locationKey(current.p);
    if (current.cost !== costs.get(key)) continue;
    if (same(current.p, goal)) {
      const path: StaffLocation[] = [];
      let step = current.p;
      while (!same(step, start)) {
        path.push(step);
        step = parents.get(locationKey(step))!;
      }
      return path.reverse();
    }
    const door = objects(state, current.p.room).find((o) => o.kind === 'door' && o.x === current.p.x && o.y === current.p.y);
    const next: { p: StaffLocation; cost: number }[] = [];
    if (door?.target) {
      const p = { room: door.target.room, ...door.target.tile };
      if (canOccupy(state, employee, p)) next.push({ p, cost: 1 });
    } else {
      for (const dir of DIRS) {
        if (canStep((x, y) => canOccupy(state, employee, { room: current.p.room, x, y }), current.p, dir.dx, dir.dy)) {
          next.push({ p: { room: current.p.room, x: current.p.x + dir.dx, y: current.p.y + dir.dy }, cost: dir.diagonal ? Math.SQRT2 : 1 });
        }
      }
    }
    for (const entry of next) {
      const nextKey = locationKey(entry.p);
      const cost = current.cost + entry.cost;
      if (cost >= (costs.get(nextKey) ?? Infinity)) continue;
      costs.set(nextKey, cost);
      parents.set(nextKey, current.p);
      open.push({ p: entry.p, cost });
    }
  }
  return null;
}

export function staffAtTarget(employee: Employee): boolean {
  const c = continuous(employee);
  if (!employee.location || !employee.navTarget || employee.navStep || c.continuousPath?.length) return false;
  if (c.position && c.position.room === employee.navTarget.room) {
    const end = tilePoint(employee.navTarget);
    return Math.hypot(c.position.x - end.x, c.position.y - end.y) <= 2;
  }
  return same(employee.location, employee.navTarget);
}

/** Reserve a randomly selected reachable side of a station once per visit. */
export function reserveStaffTarget(state: SaveState, employee: Employee, destination: StaffDestination, rng: () => number, purpose: StaffNavTarget['purpose'] = 'work'): boolean {
  if (!employee.location && !ensureStaffPosition(state, employee)) return false;
  const candidates = stationPositions(state, destination).filter((p) => canOccupy(state, employee, p));
  const target = employee.navTarget;
  if (target && target.room === destination.room && target.objectId === destination.objectId && target.purpose === purpose && candidates.some((p) => same(p, target))) return true;
  const start = employee.navStep?.to ?? employee.location!;
  // Shuffle first so equal/reachable alternatives do not always crowd one side.
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.max(0, Math.floor(rng() * (i + 1))));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }
  for (const candidate of candidates) {
    const path = findStaffPath(state, employee, start, candidate);
    if (!path) continue;
    employee.navTarget = { ...candidate, objectId: destination.objectId, purpose };
    employee.navPath = path;
    continuous(employee).continuousPath = undefined;
    employee.navigationBlocked = null;
    return true;
  }
  employee.navTarget = undefined;
  employee.navPath = undefined;
  employee.navigationBlocked = candidates.length ? `Path to ${staffObject(state, destination)?.label ?? destination.objectId} is blocked` : 'All station positions are occupied';
  return false;
}


function actorRoom(state:SaveState,e:Employee,room:RoomId){
 const actors=state.staff.employees.filter(a=>a.id!==e.id&&a.presentToday&&(a.position?.room??a.location?.room)===room).map(a=>a.position??tilePoint(a.location!));
 if(state.room===room){const t=pointTile(state.playerPosition);actors.push(t.x===state.playerTile.x&&t.y===state.playerTile.y?state.playerPosition:tilePoint(state.playerTile));}
 return withActors(ROOMS[room],actors);
}

/** Smoothed continuous travel; coarse room edges still define legal transitions. */
export function advanceStaffMovement(state:SaveState,employee:Employee,minutes:number):boolean {
 if(!ensureStaffPosition(state,employee))return false;
 syncPosition(employee);
 if(!employee.location||!employee.position||!employee.navTarget)return false;
 let budget=Math.max(0,minutes)*STAFF_TILES_PER_MINUTE*64;
 let guard=0;
 while(budget>1e-8&&guard++<100){
  if(staffAtTarget(employee)){employee.navigationBlocked=null;return true;}
  const current: Point & {room:RoomId}=employee.position!;
  const room:RoomId=current.room;
  if(!employee.continuousPath?.length){
   const coarse=findStaffPath(state,employee,employee.location,employee.navTarget);
   if(!coarse){employee.navigationBlocked='Route blocked — waiting for a clear path';return false;}
   employee.navPath=coarse;
   const nextRoom=coarse.findIndex(p=>p.room!==room);
   if(nextRoom===0){
    const next=coarse[0];
    if(!canOccupy(state,employee,next)){employee.navigationBlocked='Doorway occupied';return false;}
    employee.position={room:next.room,...tilePoint(next)};employee.location={...next};employee.navPath=undefined;budget=Math.max(0,budget-64);continue;
   }
   const endpoint=nextRoom>0?coarse[nextRoom-1]:employee.navTarget;
   const fine=route(ROOMS[room],state.skills,current,tilePoint(endpoint));
   if(!fine){employee.navigationBlocked='No clear approach';return false;}
   employee.continuousPath=fine;employee.navStep=undefined;
  }
  const next=employee.continuousPath[0],d=pixelDistance(current,next),used=Math.min(d,budget),t=d?used/d:1;
  const pos:Point={x:current.x+(next.x-current.x)*t,y:current.y+(next.y-current.y)*t};
  if(!segmentClear(actorRoom(state,employee,room),state.skills,current,pos)){employee.continuousPath=undefined;employee.navigationBlocked='Route changed';return false;}
  employee.position={room,...pos};employee.location={room,...pointTile(pos)};employee.jobPosition={...pointTile(pos)};
  budget-=used;employee.navigationBlocked=null;
  if(t>=1)employee.continuousPath.shift();else break;
 }
 return staffAtTarget(employee);
}
